import { useState } from 'react';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import { PageHeader } from '@/components/kit';
import { BackLink, recordGate } from '@/components/record';
import CustomerForm, { customerToForm } from '@/components/admin/CustomerForm';
import { CUSTOMER_FIELDS } from '@/shared/fields';
import { committed } from '@/shared/data';
import { findAccount, lowerEmail, saveAccount, splitName } from '@/shared/userAccounts';

export const route = { path: '/admin/customers/:id/edit', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };

export default function EditCustomer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const { data: customer, loading, error, run } = useFetch(
    () => sdk.table('customers_c').select(CUSTOMER_FIELDS).get(id).then((r) => r.data ?? null),
    [id],
  );

  const back = <BackLink to={`/admin/customers/${id}`} label="Back to Customer" />;
  const gate = recordGate({ back, loading, record: customer, error, run, what: 'Customer' });
  if (gate) return gate;

  async function save(payload) {
    setSaving(true);
    try {
      const emailChanged = lowerEmail(payload.email_c) !== lowerEmail(customer.email_c);
      const nameChanged = payload.Name !== customer.Name;
      if (emailChanged || nameChanged) {
        // Keep the customer's sign-in account (if they have one) in step with the record.
        const account = await findAccount({ email: customer.email_c });
        if (account?.Id) {
          const { firstName, lastName } = splitName(payload.Name);
          const result = await saveAccount(account, { firstName, lastName, email: payload.email_c });
          if (!result.ok) {
            toast.warning('Their sign-in account was not updated', { description: result.message });
            if (emailChanged) payload = { ...payload, email_c: customer.email_c };
          } else if (result.emailRejected) {
            toast.warning('Sign-in email stayed the same', { description: result.emailRejected });
            payload = { ...payload, email_c: customer.email_c };
          }
        }
      }
      const res = await sdk.table('customers_c').update({ Id: customer.Id, ...payload });
      if (!committed(res, 'Customer saved')) return;
      navigate(`/admin/customers/${customer.Id}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl">
      {back}
      <PageHeader eyebrow="Edit customer" title={customer.Name} />
      <CustomerForm
        key={customer.Id}
        initial={customerToForm(customer)}
        onSubmit={save}
        saving={saving}
        submitLabel="Save changes"
        cancelTo={`/admin/customers/${customer.Id}`}
      />
    </div>
  );
}
